import React, { CSSProperties, Fragment, useMemo } from 'react';
import AudiogramSpeakerImage from './AudiogramSpeakerImage';
import RemotionWaveform from './RemotionWaveform';
import SpeakerLabelContainer from '../SpeakerLabel/SpeakerLabelContainer';
import VideoInCompositeSegment from '../SegmentComponent/VideoInCompositeSegment';
import { BaseConfig } from '@/player-config/src/types/speaker-types';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { LayoutChange } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/LayoutChange';
import { TEXT_COLOR_DEFAULT } from '@/player-config/src/constants/colors';

const AudiogramComponent: React.FC<{
  layoutDetailsAtCurrentFrame: LayoutChange;
  segment: CompositeSegment;
  backgroundStyle: CSSProperties;
  canEditSpeakerLabel: boolean;
  commonStartFromForElements: number;
  finalConfig: BaseConfig[];
}> = ({
  layoutDetailsAtCurrentFrame,
  segment,
  backgroundStyle,
  canEditSpeakerLabel,
  commonStartFromForElements,
  finalConfig
}) => {
  const videosForAudiogram = useMemo(() => {
    return segment.videos.map(video => {
      const mutedVideoIds = layoutDetailsAtCurrentFrame?.mutedVideosId ?? [];

      const muted = mutedVideoIds.includes(video.id);

      return (
        <VideoInCompositeSegment
          key={video.id}
          video={video}
          videoIndex={-1}
          compositeSegmentId={segment.id}
          finalConfig={finalConfig}
          hasScreenshareAtCurrentFrame={!!layoutDetailsAtCurrentFrame?.hasScreenshare}
          commonStartFromForElements={commonStartFromForElements}
          canEditSpeakerLabel={canEditSpeakerLabel}
          canManuallyReposition={false}
          cropInformation={layoutDetailsAtCurrentFrame?.videoCropPositionMap?.[video.id] || null}
          muted={muted}
          contentId={segment.contentId}
        />
      );
    });
  }, [
    canEditSpeakerLabel,
    commonStartFromForElements,
    finalConfig,
    layoutDetailsAtCurrentFrame?.hasScreenshare,
    layoutDetailsAtCurrentFrame?.mutedVideosId,
    layoutDetailsAtCurrentFrame?.videoCropPositionMap,
    segment.id,
    segment.videos
  ]);

  const video = segment.videos.find(video => layoutDetailsAtCurrentFrame.videoIds.includes(video.id));
  if (!video) {
    return (
      <div className="size-full" style={backgroundStyle}>
        {videosForAudiogram}
      </div>
    );
  }

  const imgUrl = video?.speakerDetails?.profile_picture_url ?? null;
  const waveformColor = video?.speakerLabel?.style?.color ?? TEXT_COLOR_DEFAULT;

  return (
    <div className="size-full" style={backgroundStyle}>
      {videosForAudiogram}
      <Fragment>
        {layoutDetailsAtCurrentFrame.size === 'LANDSCAPE' ? (
          <div className="flex size-full">
            <div className="aspect-auto h-full w-[50%]">
              <AudiogramSpeakerImage imgUrl={imgUrl} speaker={video.speakerDetails} />
            </div>
            <div className="flex w-[50%] flex-col flex-wrap items-end justify-end p-[2.5%]">
              <RemotionWaveform textColor={waveformColor} height={100} />
            </div>
          </div>
        ) : layoutDetailsAtCurrentFrame.size === 'SQUARE' ? (
          <div className="flex size-full flex-col">
            <div className="aspect-auto size-[85%]">
              <AudiogramSpeakerImage imgUrl={imgUrl} speaker={video.speakerDetails} />
            </div>
            <div className="flex h-[15%] flex-1 flex-col items-end justify-center pr-[2.5%]">
              <RemotionWaveform textColor={waveformColor} height={100} />
            </div>
          </div>
        ) : (
          <div className="size-full">
            <div className="aspect-auto h-[70%] w-full">
              <AudiogramSpeakerImage imgUrl={imgUrl} speaker={video.speakerDetails} />
            </div>
            <div className="flex h-[30%] flex-1 items-end justify-end">
              <div className="flex h-[33%] flex-1 items-center justify-end pr-[2.5%]">
                <RemotionWaveform textColor={waveformColor} height={100} />
              </div>
            </div>
          </div>
        )}
        {video.speakerDetails && (
          <SpeakerLabelContainer
            speakerLabel={video.speakerLabel}
            canEditSpeakerLabel={canEditSpeakerLabel}
            speakerId={video.speakerDetails.id}
            isAudiogram={true}
          />
        )}
      </Fragment>
    </div>
  );
};

export default AudiogramComponent;
